import { TextStyle, Avatar, Button, Badge } from '@superops/ui-design-system';
import {
  AccessControl,
  COMMON_CONSTANTS,
  DateTimeFormatter,
  DeleteModal,
  EventEmitter,
  getTranslatedText
} from '@nile/components';
import Tippy from '@tippyjs/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { convertMinutesToHours } from '@nile/mangifera/src/Utils/helpers';
import { Tooltip } from 'react-clone-tippy';
import React, { useEffect, useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import WorklogManualTimer from './WorklogManualTimer';
import WorklogTask from './WorklogTask';

const AvatarHolder = styled.div`
  top: 0px;
  left: -30px;
`;

const WorklogEditPopupContainer = styled.div`
  background: var(--sops-worklog-timer-update-bg);
  .notes-editor .Superops-TextField__Input {
    min-height: 100px;
    max-height: calc(100vh - 500px);
  }
`;

function AddedWorklogEntry(props) {
  const {
    log,
    intl,
    deleteWorklog,
    workItem,
    ticket,
    project,
    module,
    hideTask,
    removeTaskBorder,
    editTippyZIndex
  } = props;
  const tippyRef = React.useRef(null);
  let timeout = null;
  const [mounted, setMounted] = useState(false);
  const popupParentName = 'WorklogEntryPopup';

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName)?.length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy?.state?.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  const togglePopover = () => {
    if (tippyRef?.current?._tippy) {
      if (!tippyRef.current?._tippy?.state?.isVisible) {
        setMounted(true);
        closeAllOpenPopups();
        tippyRef.current._tippy.show();
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          tippyRef.current._tippy.popperInstance.forceUpdate();
        }, 2000);
      } else {
        // EventEmitter.dispatch('resetWorklogContent');
        setMounted(false);
        tippyRef.current._tippy.hide();
      }
      // let popper = tippyRef.current._tippy?.popper || null;
      // popper.style.transform = 'translate(-440px, -50%)';
      // popper.style.top = '-50%';
    }
    // setPopoverActive(!popoverActive);
  };

  function repositionPopup() {
    if (tippyRef.current._tippy) {
      tippyRef.current._tippy.popperInstance.forceUpdate();
    }
  }
  return (
    <Tippy
      content={
        mounted ? (
          <WorklogEditPopupContainer
            className="min-width-500 br10"
            onChange={repositionPopup}
          >
            <h4 className="pl20 pt10">
              <TextStyle variation="strong">
                <FormattedMessage id="workItem.details.worklog.edit" />
              </TextStyle>
            </h4>
            <WorklogManualTimer
              ticket={ticket}
              project={project}
              timer={log}
              handleClose={togglePopover}
              module={module}
              workItem={workItem}
              workLogEdit
              hideWorkitem
              repositionPopup={repositionPopup}
            />
          </WorklogEditPopupContainer>
        ) : null
      }
      trigger="manual" // manually close the tip on api calls
      interactive
      placement="left"
      arrow={false}
      theme="light"
      allowHTML
      ref={(node) => {
        tippyRef.current = node;
      }}
      appendTo={document.body}
      offset={[30, 30]}
      hideOnClick="toggle"
      maxWidth={500}
      zIndex={editTippyZIndex || 400}
    >
      <div className={`${popupParentName}`} key={log?.itemId}>
        <Worklog {...props} togglePopover={togglePopover} />
      </div>
    </Tippy>
  );

  // return (
  //   <Popover
  //     activator={<Worklog {...props} togglePopover={togglePopover} />}
  //     active={popoverActive}
  //     preferredAlignment="left"
  //     preferredPosition="mostSpace"
  //     ref={(node) => {
  //       tippyRef.current = node;
  //     }}
  //   >
  //     {/* {mounted ? ( */}
  //     <WorklogEditPopupContainer>
  //       <WorklogManualTimer
  //         ticket={ticket}
  //         project={project}
  //         log={log}
  //         handleClose={togglePopover}
  //         module={module}
  //       />
  //     </WorklogEditPopupContainer>
  //     {/* ) : null} */}
  //   </Popover>
  // );
}

function ToolTipWrapper({ title, children }) {
  return (
    <Tooltip
      title={title}
      inertia
      trigger="mouseenter"
      animation="shift"
      animateFill={false}
      position="top-start"
      style={{ display: 'flex' }}
      unmountHTMLWhenHide
    >
      {children}
    </Tooltip>
  );
}

function Worklog(props) {
  const {
    log,
    intl,
    deleteWorklog,
    togglePopover,
    hideTask,
    removeTaskBorder
  } = props;
  const flags = useFlags();
  const [showLess, setShowLess] = useState(true);
  const isDisabled = log.status !== 'PENDING';
  const trimNoOfLines = (textData, trim) => {
    if (textData && trim) {
      return textData
        .split('')
        .slice(0, 300)
        .join('');
    }
    return textData;
  };
  return (
    <div
      className={`flex col g10 action-show rounded-2xl pt-5 pl20 pb16 pr-15 bg-gray ${
        log?.task && !removeTaskBorder ? 'border-black' : ''
      }`}
    >
      <div className="flex center relative wrap">
        <AvatarHolder className="position-absolute mr-5">
          <Avatar size="extraSmall" initials={log.technician.name[0]} />
        </AvatarHolder>
        <div className="flex center fg1 wrap">
          <div className="mr-5">
            <TextStyle variation="strong">
              <div className="ellipsis">{log.technician.name}</div>
            </TextStyle>
          </div>
          <TextStyle variation="subdued">
            <div className="ellipsis f-12">
              <DateTimeFormatter type="dateTime" date={log.billDateTime} />
            </div>
          </TextStyle>
        </div>
        {!isDisabled && (
          <div className="flex center g10 action-visibility pr5 ml-auto">
            <DeleteModal
              description={getTranslatedText(
                intl,
                'ticket.details.worklog.delete.description'
              )}
              handleDeleteConfirmation={() => {
                deleteWorklog(log.itemId);
              }}
              title={getTranslatedText(
                intl,
                'ticket.details.worklog.delete.title'
              )}
              plain
            />
            <div className="pointer" onClick={togglePopover}>
              <SVGIcon name="Edit" />
            </div>
          </div>
        )}
      </div>
      <div className="flex col">
        <div className="flex center wrap g10">
          {log?.worklogTimerType === 'MANUAL' && (
            <Tooltip
              title={getTranslatedText(intl, 'workItem.manual.entry')}
              delay={400}
              duration={250}
              position="top-start"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <div className="f-12">
                <Badge status="attention">M</Badge>
              </div>
            </Tooltip>
          )}
          <TextStyle variation="strong">
            {convertMinutesToHours(Number(log.revisedQuantity * 60))}
          </TextStyle>
          {log.afterHours && (
            <ToolTipWrapper
              title={getTranslatedText(intl, 'worklog.details.item.afterHours')}
            >
              <SVGIcon name="AfterHours" />
            </ToolTipWrapper>
          )}
          {log.billable && (
            <AccessControl
              productToCheck={COMMON_CONSTANTS.MSP}
              render={
                <ToolTipWrapper
                  title={getTranslatedText(
                    intl,
                    'worklog.details.item.billable'
                  )}
                >
                  <SVGIcon name="Billable" />
                </ToolTipWrapper>
              }
            />
          )}
          {isDisabled && (
            <ToolTipWrapper
              title={getTranslatedText(
                intl,
                'worklog.details.item.invoicedOrAudited'
              )}
            >
              <SVGIcon name="InvoiceLogo" />
            </ToolTipWrapper>
          )}
        </div>
        {log?.qty && (
          <TextStyle variation="subdued">
            <span className="fs10">
              <FormattedMessage id="attribute.worklog.recordedTime" />:{' '}
              {convertMinutesToHours(Number(log.qty * 60))}
            </span>
          </TextStyle>
        )}
      </div>
      <AccessControl
        productToCheck="MSP"
        render={
          <>
            {(log?.serviceCatalogItem || log?.serviceItem) && (
              <div className="flex center">
                <SVGIcon name="Cart" />
                <span className="ml-5">
                  {log?.serviceCatalogItem?.name || log?.serviceItem?.name}
                </span>
              </div>
            )}
          </>
        }
      />

      {log?.task && !hideTask && (
        <WorklogTask
          task={log.task}
          workItem={log?.workItem}
          hideTaskWorkItem
          onEditCompleted={() => {
            EventEmitter.dispatch('refetchWorklogs');
          }}
        />
      )}

      <div className="flex center g10">
        <div className="fg1 word-break white-space-pre-line">
          {log.notes
            ? trimNoOfLines(log.notes, showLess)
            : getTranslatedText(intl, 'ticket.details.worklog.noNotes')}
          {log?.notes && log?.notes?.length > 300 && (
            <Button onClick={() => setShowLess(!showLess)} plain>
              {showLess ? (
                <>
                  ...
                  {getTranslatedText(
                    intl,
                    'worklog.details.item.notes.showMore'
                  )}
                </>
              ) : (
                <span className="ml-5">
                  {getTranslatedText(
                    intl,
                    'worklog.details.item.notes.showLess'
                  )}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default injectIntl(AddedWorklogEntry);
